/**
 * Settings API client for Neuro-HA.
 *
 * Provides functions for:
 * - Fetching and updating AI model settings
 * - Listing available Gemini models
 */

import api from './client'

// ---- Types ----

export interface ModelSettings {
  coordinator_model: string
  specialist_model: string
  reasoning_model: string
}

export interface GeminiModel {
  name: string
  display_name: string
  description: string
  input_token_limit: number | null
  output_token_limit: number | null
  supported_generation_methods: string[]
}

export interface ModelSettingsResponse {
  ok: boolean
  models: ModelSettings
}

export interface AvailableModelsResponse {
  ok: boolean
  models: GeminiModel[]
  count: number
  source?: string
  error?: string
}

// ---- API Functions ----

/**
 * Get current AI model settings.
 */
export async function getModelSettings(): Promise<ModelSettings> {
  const response = await api.get<ModelSettingsResponse>('/settings/models')
  return response.data.models
}

/**
 * Update AI model settings.
 *
 * @param settings - Partial settings to update (only provided fields change)
 */
export async function updateModelSettings(
  settings: Partial<ModelSettings>
): Promise<ModelSettings> {
  const response = await api.put<ModelSettingsResponse>('/settings/models', settings)
  return response.data.models
}

/**
 * Get list of available Gemini models.
 *
 * Fetches from Gemini API if available, otherwise returns defaults.
 */
export async function getAvailableModels(): Promise<GeminiModel[]> {
  const response = await api.get<AvailableModelsResponse>('/settings/models/available')
  return response.data.models
}

/**
 * Get both current settings and available models.
 *
 * Convenience function for the settings page.
 */
export async function getModelSettingsWithOptions(): Promise<{
  settings: ModelSettings
  models: GeminiModel[]
}> {
  const [settings, models] = await Promise.all([
    getModelSettings(),
    getAvailableModels(),
  ])
  return { settings, models }
}
